import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import PageHero from "@/components/feature/PageHero";
import TrustBar from "@/components/feature/TrustBar";
import ServicesGrid from "@/components/feature/ServicesGrid";
import AIAnswerBlock from "@/components/feature/AIAnswerBlock";
import ProcessSection from "@/components/feature/ProcessSection";
import WhyChooseUs from "@/components/feature/WhyChooseUs";
import ServiceAreas from "@/components/feature/ServiceAreas";
import TestimonialsSection from "@/components/feature/TestimonialsSection";
import FAQSection from "@/components/feature/FAQSection";
import FinalCTA from "@/components/feature/FinalCTA";
import LeadForm from "@/components/feature/LeadForm";
import { services } from "@/mocks/services";
import { servicesFAQ } from "@/mocks/faq";
import { useScrollReveal } from "@/hooks/useScrollReveal";

// Unique hero image for Services Page — premium vehicle lineup golden hour
const HERO_IMAGE =
  "https://public.readdy.ai/ai/img_res/a6b5f2bafb8bb1dae080fc5865bf4b44.jpg";

const servicesAIItems = [
  {
    icon: "ri-car-line",
    question: "What types of used vehicles does United Auto Group carry?",
    answer:
      "United Auto Group carries a diverse inventory of quality pre-owned SUVs, sedans, pickups, coupes, vans, and wagons. Popular brands include BMW, Mercedes-Benz, Audi, Honda, Toyota, Ford, Chevrolet, Jeep, and more — all inspected and warranty-backed.",
  },
  {
    icon: "ri-bank-card-line",
    question: "What financing options are available for used car purchases?",
    answer:
      "United Auto Group offers flexible financing through multiple lender relationships. Options are available for all credit situations — good credit, bad credit, and first-time buyers. Apply online or visit the dealership for same-day approval decisions.",
  },
  {
    icon: "ri-shield-check-line",
    question: "What warranty protection comes with every vehicle?",
    answer:
      "Every vehicle purchase includes a complimentary 15-day warranty. This covers mechanical issues discovered after purchase, giving you complete confidence. Extended warranty packages are available for long-term protection beyond the initial 15 days.",
  },
  {
    icon: "ri-steering-2-line",
    question: "Can I test drive a vehicle before committing to purchase?",
    answer:
      "Absolutely. United Auto Group encourages every customer to test drive before buying. Their no-pressure sales team will walk you through every feature and answer all questions. Call (952) 769-3208 or visit 1024 8th St, Farmington, MN to schedule your test drive.",
  },
  {
    icon: "ri-search-eye-line",
    question: "What if I cannot find the vehicle I am looking for?",
    answer:
      "Use our Car Finder service. Tell us your exact make, model, budget, and feature requirements. Our team actively searches to locate the right vehicle for you — at no extra charge and with no obligation.",
  },
  {
    icon: "ri-percent-line",
    question: "Does United Auto Group offer competitive pricing?",
    answer:
      "Yes. We price every vehicle fairly and transparently — no bait-and-switch, no hidden fees. What you see is what you pay. We respect your budget and your time with honest upfront pricing on every vehicle.",
  },
];

const serviceWhyFeatures = [
  {
    icon: "ri-check-double-line",
    title: "Every Vehicle Thoroughly Inspected",
    description:
      "Our team inspects every vehicle before it reaches the lot. We only carry vehicles that meet our quality standards so you can shop with complete confidence.",
  },
  {
    icon: "ri-shield-check-line",
    title: "15-Day Warranty on Every Purchase",
    description:
      "No other dealership in the area backs every sale with a 15-day warranty. It is our commitment to your satisfaction and peace of mind.",
  },
  {
    icon: "ri-percent-line",
    title: "Competitive, Honest Pricing",
    description:
      "We price our vehicles fairly and transparently. No bait-and-switch, no hidden fees. What you see is what you pay — and we negotiate respectfully.",
  },
  {
    icon: "ri-customer-service-2-line",
    title: "Post-Purchase Support",
    description:
      "Our relationship does not end at the sale. Our team is available after your purchase to answer questions and ensure you are completely satisfied.",
  },
];

function VehicleHighlight() {
  const { ref, isVisible } = useScrollReveal();

  return (
    <section className="bg-cream-DEFAULT py-16 md:py-20" aria-labelledby="vehicle-highlight-heading">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div
          ref={ref}
          className={`transition-all duration-700 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <h2
            id="vehicle-highlight-heading"
            className="font-serif text-charcoal text-center mb-8"
            style={{ fontSize: "clamp(1.7rem, 3vw, 2.4rem)" }}
          >
            Shop By Body Type
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
            {[
              {
                image: "https://readdy.ai/api/search-image?query=luxury%20SUV%20crossover%20premium%20vehicle%20dramatic%20golden%20hour%20mountain%20landscape%20outdoor%20adventure%20lifestyle%20automotive%20photography%20upscale%20aspirational%20quality&width=600&height=420&seq=uag-svc-suv-v8&orientation=landscape",
                label: "SUVs",
                count: "37 Available",
              },
              {
                image: "https://readdy.ai/api/search-image?query=elegant%20premium%20luxury%20sedan%20sleek%20vehicle%20city%20night%20rain%20reflections%20bokeh%20upscale%20urban%20cinematic%20automotive%20photography%20dark%20moody%20sophisticated&width=600&height=420&seq=uag-svc-sedan-v8&orientation=landscape",
                label: "Sedans",
                count: "19 Available",
              },
              {
                image: "https://readdy.ai/api/search-image?query=powerful%20pickup%20truck%20rugged%20terrain%20desert%20landscape%20dramatic%20sky%20masculine%20work%20capability%20cinematic%20automotive%20photography%20warm%20tones%20adventure&width=600&height=420&seq=uag-svc-truck-v8&orientation=landscape",
                label: "Trucks",
                count: "1 Available",
              },
              {
                image: "https://readdy.ai/api/search-image?query=sporty%20performance%20coupe%20winding%20coastal%20road%20ocean%20view%20sunset%20orange%20premium%20automotive%20lifestyle%20photography%20speed%20elegance%20curves%20cinematic%20quality&width=600&height=420&seq=uag-svc-coupe-v8&orientation=landscape",
                label: "Coupes",
                count: "1 Available",
              },
            ].map((item, i) => (
              <a
                key={item.label}
                href="https://www.uagmn.com/inventory/"
                rel="noopener noreferrer"
                className={`group relative rounded-2xl overflow-hidden cursor-pointer transition-all duration-500 hover:-translate-y-1.5 ${
                  isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
                }`}
                style={{ transitionDelay: `${i * 80}ms` }}
                aria-label={`Browse ${item.label} — ${item.count}`}
              >
                <div className="w-full h-40 md:h-52 relative">
                  <img
                    src={item.image}
                    alt={`${item.label} for sale at United Auto Group Farmington MN`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-charcoal/80 via-transparent to-transparent" />
                  <div className="absolute bottom-4 left-4">
                    <p className="text-white font-sans font-bold text-sm">{item.label}</p>
                    <p className="text-gold-DEFAULT text-xs font-sans mt-0.5">{item.count}</p>
                  </div>
                </div>
              </a>
            ))}
          </div>

          <div className="bg-white rounded-2xl p-8 md:p-10 flex flex-col md:flex-row items-center gap-6 justify-between border border-cream-dark">
            <div>
              <h3 className="font-serif text-charcoal text-2xl md:text-3xl mb-2">
                Can&apos;t Find What You&apos;re Looking For?
              </h3>
              <p className="text-charcoal-soft font-sans text-sm max-w-lg">
                Use our Car Finder service. Tell us exactly what you need and we will locate the perfect vehicle — even if it is not currently in our inventory.
              </p>
            </div>
            <a
              href="https://www.uagmn.com/car-finder/"
              rel="noopener noreferrer"
              className="flex-shrink-0 inline-flex items-center gap-2 bg-gold-DEFAULT text-charcoal font-bold text-sm px-8 py-4 rounded-full hover:bg-gold-light transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              Use Car Finder
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-search-eye-line" />
              </div>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function ServicesPage() {
  return (
    <div className="font-sans">
      <Navbar />

      {/* 1. Hero */}
      <PageHero
        breadcrumb="Home › Services"
        title="Premium Pre-Owned Vehicles"
        titleAccent="& Complete Financing Solutions"
        subtitle="United Auto Group offers a full suite of automotive services in Farmington, MN — from a hand-picked used car inventory to flexible financing for every credit situation."
        backgroundImage={HERO_IMAGE}
        primaryCTA={{ label: "View All Inventory", href: "https://www.uagmn.com/inventory/" }}
        secondaryCTA={{ label: "Apply for Financing", href: "https://www.uagmn.com/apply-online/" }}
        fullScreen={false}
        showPhone
      />

      {/* 2. Trust Bar */}
      <TrustBar />

      {/* 3. Page-Unique: Vehicle Type Highlight + Car Finder */}
      <VehicleHighlight />

      {/* 4. Services Overview */}
      <ServicesGrid
        title="Our Complete Range of Automotive Services"
        subtitle="Every service we offer is designed to make your car-buying experience easier, more transparent, and more rewarding."
        services={services}
        eyebrow="SERVICES"
      />

      {/* 5. AI Answer Block */}
      <AIAnswerBlock
        title="What You Need to Know About Our Services"
        subtitle="Straight answers about financing, inventory, warranties, and the buying process at United Auto Group Farmington."
        items={servicesAIItems}
      />

      {/* 6. Process */}
      <ProcessSection
        title="A Streamlined Process Built Around You"
        subtitle="From first look to driving away — we have removed the friction from the car-buying experience so it is fast, clear, and enjoyable."
      />

      {/* 7. Why Choose Us */}
      <WhyChooseUs
        title="Why Our Services Stand Apart in the Twin Cities Market"
        description="We compete on quality, transparency, and personal service — not gimmicks or high-pressure tactics. Our service philosophy is simple: treat every customer the way we would want to be treated."
        features={serviceWhyFeatures}
      />

      {/* 8. Service Areas */}
      <ServiceAreas
        title="Serving Used Car Buyers Across the South Twin Cities Metro"
        subtitle="Our services reach customers from Farmington, Apple Valley, Lakeville, Burnsville, Eagan, and dozens of communities throughout Dakota and Scott counties."
      />

      {/* 9. Testimonials */}
      <TestimonialsSection />

      {/* 10. Lead Form */}
      <LeadForm />

      {/* 11. FAQ */}
      <FAQSection
        title="Service Questions Answered"
        items={servicesFAQ}
      />

      {/* 12. Final CTA */}
      <FinalCTA
        title="Ready to Experience the United Auto Group Difference?"
        subtitle="Come see why hundreds of Twin Cities families have chosen us for their next vehicle. Quality inventory, honest pricing, flexible financing — all in Farmington, MN."
        primaryCTA={{ label: "Browse Inventory", href: "https://www.uagmn.com/inventory/" }}
      />

      <Footer />
    </div>
  );
}
